import React, { useEffect, useLayoutEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AppProvider, useApp } from "./contexts/AppContext";
import { Toaster } from "sonner";
import SnowEffect from "./components/SnowEffect";
import SparkleEffect from "./components/SparkleEffect";
import FireworksEffect from "./components/FireworksEffect";
import BackgroundMusic from "./components/BackgroundMusic";
import BlizzardEffect from "./components/BlizzardEffect";
import IntroSplash from "./components/IntroSplash";
import Header from "./components/Header";
import Footer from "./components/Footer";

import Home from "./pages/Home";
import TrainList from "./pages/TrainList";
import Compete from "./pages/Compete";
import QuizPlay from "./pages/QuizPlay";
import QuizComplete from "./pages/QuizComplete";
import Rules from "./pages/Rules";
import Results from "./pages/Results";
import EnrollTeacher from "./pages/EnrollTeacher";
import EnrollPayment from "./pages/EnrollPayment";
import DiplomaView from "./pages/DiplomaView";
import LegalPage from "./pages/LegalPage";
import AccountAuth from "./pages/AccountAuth";
import AccountDashboard from "./pages/AccountDashboard";
import FacebookCallback from "./pages/FacebookCallback";
import { AccountProvider } from "./contexts/AccountContext";
import { GoogleOAuthProvider } from "@react-oauth/google";

import AdminLogin from "./pages/Admin/AdminLogin";
import AdminLayout from "./pages/Admin/AdminLayout";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import AdminQuizzes from "./pages/Admin/AdminQuizzes";
import AdminQuestions from "./pages/Admin/AdminQuestions";
import AdminCredentials from "./pages/Admin/AdminCredentials";
import AdminResults from "./pages/Admin/AdminResults";
import AdminTexts from "./pages/Admin/AdminTexts";
import AdminSettings from "./pages/Admin/AdminSettings";
import AdminEnrollments from "./pages/Admin/AdminEnrollments";
import AdminForms from "./pages/Admin/AdminForms";
import AdminArchive from "./pages/Admin/AdminArchive";
import AdminTeacherPassword from "./pages/Admin/AdminTeacherPassword";
import AdminRankings from "./pages/Admin/AdminRankings";
import AdminPaymentCredentials from "./pages/Admin/AdminPaymentCredentials";
import AdminSideButtons from "./pages/Admin/AdminSideButtons";
import AdminEmails from "./pages/Admin/AdminEmails";
import AdminDiplomas from "./pages/Admin/AdminDiplomas";
import AdminTestZone from "./pages/Admin/AdminTestZone";
import AdminMetrics from "./pages/Admin/AdminMetrics";
import AdminEffects from "./pages/Admin/AdminEffects";
import AdminQuizAppearance from "./pages/Admin/AdminQuizAppearance";
import AdminMigration from "./pages/Admin/AdminMigration";
import AdminMainSettings from "./pages/Admin/AdminMainSettings";
import LangEntry from "./pages/LangEntry";

import "./App.css";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
  }, []);
  useLayoutEffect(() => {
    const toTop = () => {
      window.scrollTo(0, 0);
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    toTop();
    const raf = requestAnimationFrame(toTop);
    const t = setTimeout(toTop, 120);
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [pathname]);
  return null;
}

function Shell({ children }) {
  return (
    <div className="App relative">
      <SnowEffect />
      <SparkleEffect />
      <FireworksEffect />
      <BlizzardEffect />
      <IntroSplash />
      <BackgroundMusic />
      <Header />
      <main className="relative z-10">{children}</main>
      <Footer />
    </div>
  );
}

function AppRoutes() {
  const { loadedOnce } = useApp();
  if (!loadedOnce) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#0B1D3A] font-fredoka text-2xl">
        ✶ A carregar a magia...
      </div>
    );
  }
  return (
    <Routes>
      <Route path="/" element={<Shell><Home /></Shell>} />
      <Route path="/pt" element={<LangEntry lang="pt" />} />
      <Route path="/br" element={<LangEntry lang="br" />} />
      <Route path="/en" element={<LangEntry lang="en" />} />
      <Route path="/train" element={<Shell><TrainList /></Shell>} />
      <Route path="/compete" element={<Shell><Compete /></Shell>} />
      <Route path="/play/:mode/:quizId" element={<QuizPlay />} />
      <Route path="/complete" element={<Shell><QuizComplete /></Shell>} />
      <Route path="/rules" element={<Shell><Rules /></Shell>} />
      <Route path="/rules/pt" element={<LangEntry lang="pt" to="/rules" />} />
      <Route path="/rules/br" element={<LangEntry lang="br" to="/rules" />} />
      <Route path="/rules/en" element={<LangEntry lang="en" to="/rules" />} />
      <Route path="/train/pt" element={<LangEntry lang="pt" to="/train" />} />
      <Route path="/train/br" element={<LangEntry lang="br" to="/train" />} />
      <Route path="/train/en" element={<LangEntry lang="en" to="/train" />} />
      <Route path="/enroll/pt" element={<LangEntry lang="pt" to="/enroll" />} />
      <Route path="/enroll/br" element={<LangEntry lang="br" to="/enroll" />} />
      <Route path="/enroll/en" element={<LangEntry lang="en" to="/enroll" />} />
      <Route path="/results" element={<Shell><Results /></Shell>} />
      <Route path="/enroll" element={<Shell><EnrollTeacher /></Shell>} />
      <Route path="/enroll/payment/:id" element={<Shell><EnrollPayment /></Shell>} />
      <Route path="/diploma/view/:id" element={<Shell><DiplomaView /></Shell>} />
      <Route path="/terms" element={<Shell><LegalPage type="terms" /></Shell>} />
      <Route path="/privacy" element={<Shell><LegalPage type="privacy" /></Shell>} />
      <Route path="/account" element={<Shell><AccountAuth /></Shell>} />
      <Route path="/account/dashboard" element={<Shell><AccountDashboard /></Shell>} />
      <Route path="/facebook/callback" element={<FacebookCallback />} />

      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="main-settings" element={<AdminMainSettings />} />
        <Route path="quizzes" element={<AdminQuizzes />} />
        <Route path="quizzes/:quizId/questions" element={<AdminQuestions />} />
        <Route path="teacher-password" element={<AdminTeacherPassword />} />
        <Route path="archive" element={<AdminArchive />} />
        <Route path="rankings" element={<AdminRankings />} />
        <Route path="quizzes/:quizId/credentials" element={<AdminCredentials />} />
        <Route path="quizzes/:quizId/results" element={<AdminResults />} />
        <Route path="texts" element={<AdminTexts />} />
        <Route path="enrollments" element={<AdminEnrollments />} />
        <Route path="payment-credentials" element={<AdminPaymentCredentials />} />
        <Route path="forms" element={<AdminForms />} />
        <Route path="payments" element={<Navigate to="/admin/enrollments" replace />} />
        <Route path="emails" element={<AdminEmails />} />
        <Route path="diplomas" element={<AdminDiplomas />} />
        <Route path="side-buttons" element={<AdminSideButtons />} />
        <Route path="test-zone" element={<AdminTestZone />} />
        <Route path="metrics" element={<AdminMetrics />} />
        <Route path="settings-x" element={<AdminSettings variant="x" />} />
        <Route path="effects" element={<AdminEffects />} />
        <Route path="quiz-appearance" element={<AdminQuizAppearance />} />
        <Route path="migration" element={<AdminMigration />} />
      </Route>
    </Routes>
  );
}

function App() {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
  const tree = (
    <AppProvider>
      <AccountProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppRoutes />
          <Toaster richColors position="top-center" />
        </BrowserRouter>
      </AccountProvider>
    </AppProvider>
  );
  return googleClientId ? (
    <GoogleOAuthProvider clientId={googleClientId}>{tree}</GoogleOAuthProvider>
  ) : tree;
}

export default App;
